import { DateTime } from 'luxon'
import { Address, addressFromDto, AddressDto } from '../../addresses/models/address'

export interface BuildingDisplaySingleFieldConfig<T> {
    type: 'field'
    value: keyof T
}

export interface BuildingDisplayExpressionFieldConfig {
    type: 'expression'
    value: string
}

export type BuildingDisplayFieldConfig<T> = BuildingDisplaySingleFieldConfig<T> | BuildingDisplayExpressionFieldConfig

export const defaultDisplayNameConfig: BuildingDisplaySingleFieldConfig<Building> = {
    type: 'field',
    value: 'name'
}

export const buildingNestedValue = (obj: unknown, path: string): string | undefined => {
    if (typeof obj !== 'object' || obj === null) {
        return undefined
    }

    const keys = path.split('.')
    let currentValue: unknown = obj
    for (const key of keys) {
        if (currentValue && typeof currentValue === 'object' && key in currentValue) {
            currentValue = (currentValue as Record<string, unknown>)[key]
        }
    }

    return typeof currentValue === 'string' ? currentValue : undefined
}

export const buildingDisplayName = (building: Building, config: BuildingDisplayFieldConfig<Building> = defaultDisplayNameConfig): string => {
    if (config.type === 'field') {
        return building[config.value] as string
    }

    if (config.type === 'expression') {
        return config.value.replace(/\${([^}]+)}/g, (_, key) => {
            const value = buildingNestedValue(building, key)
            return value !== undefined ? value : ''
        })
    }

    return building.name
}

declare const BuildingIDSymbol: unique symbol
export type BuildingID = string & { [BuildingIDSymbol]: never }

export interface BuildingDto {
    id: string
    name: string
    archived_at: string | null

    area_count: number

    permissions: { can_edit: boolean }

    address?: { data: AddressDto }

    custom_fields?: Record<string, string>
}

export interface Building {
    id: BuildingID
    name: string
    archivedAt: DateTime | null

    areaCount: number

    permissions: { canEdit: boolean }

    address: Address | null
    addressFull: string | null

    customFields: Record<string, string>
}

export const createBuilding = (
    params: {
        id: BuildingID,
        name: string,
        archivedAt: DateTime | null,

        areaCount: number,

        permissions: { canEdit: boolean },

        address: Address | null,

        customFields?: Record<string, string>
    }
): Building => ({
    id: params.id,
    name: params.name,
    archivedAt: params.archivedAt,

    areaCount: params.areaCount,

    permissions: params.permissions,

    address: params.address,
    addressFull: params.address ? `${params.address.addressLine1}, ${params.address.addressLine2 ?? ''}, ${params.address.city}, ${params.address.postCode}, ${params.address.state ?? ''}, ${params.address.country}` : null,

    customFields: params.customFields ?? {},
})

export const buildingFromDto = (dto: BuildingDto): Building => (createBuilding({
    id: dto.id as BuildingID,
    name: dto.name,
    archivedAt: dto.archived_at ? DateTime.fromISO(dto.archived_at) : null,

    areaCount: dto.area_count,

    permissions: { canEdit: dto.permissions.can_edit },

    address: dto.address?.data ? addressFromDto(dto.address.data) : null,

    customFields: dto.custom_fields ?? {}
}))
